import React, { useState } from 'react';
import Button from './Button';
import { useToast } from '../contexts/ToastContext';
import { WriteCapabilityLevel } from '../contexts/WriteGuardContext';

export interface SaveButtonProps {
    /** Async save handler. Contract:
     *    - Resolve (any value ≠ `false`) → success path runs.
     *    - Resolve with explicit `false` → stay on the page (validation aborted;
     *      the handler is expected to have surfaced the reason itself).
     *    - Reject (throw) → stay on the page. If `formatError` is provided the
     *      button surfaces the error as a toast centrally; otherwise the caller
     *      is expected to surface it inside the handler (legacy contract).
     */
    onSave: () => Promise<boolean | void>;
    /** Centralized error display: when provided, a rejected `onSave` is caught
     *  and `toast.error(formatError(err))` is shown — so handlers can simply
     *  `throw` instead of duplicating toast logic. Omit to keep the legacy
     *  "handler toasts itself" behaviour. */
    formatError?: (err: unknown) => string;
    /** Called after a successful save. Use it to refresh data in place or
     *  to navigate away (e.g. for a "create new" flow). */
    onSuccess?: () => void | Promise<void>;
    /** Form is currently considered "dirty" (has unsaved changes). When
     *  `false`, the button is disabled even if `disabled` is not set.
     *  When `true` or `undefined`, the original behaviour applies. */
    dirty?: boolean;
    /** Extra disable predicate — combined with the internal saving state. */
    disabled?: boolean;
    /** Role capability this save requires (default `'write'`). Pass `'config'`
     *  for settings/configuration saves so it blocks for workers too. The
     *  button auto-disables + tooltips for read-only users via WriteGuard. */
    requires?: WriteCapabilityLevel;
    /** Override button label (default: `"Save"`). */
    saveLabel?: string;
    /** Label shown briefly after a successful save (default: `"Saved"`). */
    savedLabel?: string;
    /** Override button content entirely. Wins over `saveLabel`. */
    children?: React.ReactNode;
    /** When true (default), only the icon shows below `md`; the label appears
     *  from `md` (768px) up. */
    responsiveLabel?: boolean;
    className?: string;
    /** `data-testid` on the button. Defaults to `save-button`, which is what
     *  the Playwright journey looks for; pass `undefined`-safe overrides when a
     *  page renders more than one. */
    testId?: string;
}

const SUCCESS_ANIMATION_MS = 1500;

/**
 * Standard save button — shared across tenant, admin and web.
 *
 * Visual states:
 *   - **Pristine** (`dirty === false`): disabled, gray, can't be clicked.
 *   - **Dirty**: enabled, primary orange.
 *   - **Saving**: disabled, sync icon.
 *   - **Saved** (1.5s after success): green pulse animation.
 *
 * The button never navigates if the promise throws or resolves with `false`,
 * so callers can do "early return on validation" without side effects.
 */
const SaveButton: React.FC<SaveButtonProps> = ({
    onSave,
    onSuccess,
    formatError,
    dirty,
    disabled,
    requires = 'write',
    saveLabel = 'Save',
    savedLabel = 'Saved',
    children,
    responsiveLabel = true,
    className,
    testId = 'save-button',
}) => {
    const { toast } = useToast();
    const [saving, setSaving] = useState(false);
    const [succeeded, setSucceeded] = useState(false);

    const handleClick = async () => {
        setSaving(true);
        try {
            const result = await onSave();
            if (result === false) return;
            try {
                await onSuccess?.();
            } catch {
                // Refresh failures shouldn't break the success animation —
                // the data was saved server-side either way.
            }
            setSucceeded(true);
            window.setTimeout(() => setSucceeded(false), SUCCESS_ANIMATION_MS);
        } catch (err) {
            // Stay on the page. Surface the error centrally when a formatter is
            // provided; otherwise the caller already toasted it (legacy).
            if (formatError) toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const label = succeeded ? savedLabel : (children ?? saveLabel);

    const isDisabled = disabled || dirty === false;

    // The spinner and the green pulse are Button's own `loading` / `success`.
    // A static `sync` icon was passed here before, and Button only animates
    // the `loading` icon — that was the "icon never spins" bug.
    return (
        <Button
            type="button"
            variant="primary"
            icon="save"
            loading={saving}
            success={succeeded}
            onClick={handleClick}
            disabled={isDisabled}
            requires={requires}
            className={className}
            data-testid={testId}
            // On a phone the label is hidden and only the icon shows: keep its
            // name for screen readers (it had none).
            aria-label={responsiveLabel && typeof label === 'string' ? label : undefined}
        >
            {responsiveLabel ? <span className="hidden md:inline">{label}</span> : label}
        </Button>
    );
};

export default SaveButton;
